import { useState } from 'react';
import { Mail, MessageCircle, Send, CheckCircle2, AlertCircle, MapPin, Phone, Clock, ExternalLink } from 'lucide-react';
import { useLang, useSeo } from '@/i18n/LanguageContext';
import PageHero from '@/components/PageHero';
import { Reveal } from '@/components/Reveal';

const API = process.env.REACT_APP_BACKEND_URL;

const osmEmbed = (lat, lon) => {
  const d = 0.02;
  return `https://www.openstreetmap.org/export/embed.html?bbox=${lon - d}%2C${lat - 0.012}%2C${lon + d}%2C${lat + 0.012}&layer=mapnik&marker=${lat}%2C${lon}`;
};
const osmLink = (lat, lon) => `https://www.openstreetmap.org/?mlat=${lat}&mlon=${lon}#map=14/${lat}/${lon}`;

export default function Contact() {
  const { content, lang, country, setCountry, waLink } = useLang();
  useSeo(`${content.contact.title} | Rodotrack`, content.contact.subtitle);
  const c = content.contact;

  const defaultCountry = () => {
    if (lang === 'pt-br') return 'BR';
    return country === 'ar' ? 'AR' : 'PY';
  };

  const [form, setForm] = useState({ name: '', email: '', phone: '', country: defaultCountry(), vehicle: '', message: '' });
  const [status, setStatus] = useState('idle');

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const valid = form.name.trim() && /.+@.+\..+/.test(form.email) && form.phone.trim() && form.vehicle;

  const submit = async (e) => {
    e.preventDefault();
    if (!valid || status === 'sending') return;
    setStatus('sending');
    try {
      const r = await fetch(`${API}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, lang }),
      });
      if (!r.ok) throw new Error();
      setStatus('success');
    } catch {
      setStatus('error');
    }
  };

  const inputCls = 'w-full rounded-xl border border-brand-line bg-white px-4 py-3 text-[14px] text-brand-navy placeholder:text-brand-muted/60 transition focus:border-brand-blue focus:outline-none focus:ring-2 focus:ring-brand-blue/20';
  const sedeWaLink = (sede) => `https://wa.me/${sede.phone.replace(/\D/g, '')}?text=${encodeURIComponent(content.whatsapp.message)}`;

  return (
    <>
      <PageHero badge={c.eyebrow} title={c.title} subtitle={c.subtitle} />
      <section className="py-16 lg:py-24" data-testid="contact-section">
        <div className="container-x grid gap-8 lg:grid-cols-12">
          <Reveal className="lg:col-span-7">
            <form data-testid="contact-form" onSubmit={submit} className="card-soft p-7 lg:p-10">
              {status === 'success' ? (
                <div data-testid="contact-success-msg" className="flex flex-col items-center py-14 text-center">
                  <CheckCircle2 className="h-14 w-14 text-brand-green" />
                  <p className="mt-5 max-w-sm font-display text-xl font-bold text-brand-navy">{c.form.success}</p>
                </div>
              ) : (
                <div className="grid gap-5 sm:grid-cols-2">
                  <label className="block">
                    <span className="mb-1.5 block text-[13px] font-semibold text-brand-navy">{c.form.name}</span>
                    <input data-testid="contact-input-name" className={inputCls} value={form.name} onChange={set('name')} required />
                  </label>
                 <label className="block">
  <span className="mb-1.5 block text-[13px] font-semibold text-brand-navy">
    {c.form.email}
  </span>

  <input
    data-testid="contact-input-email"
    type="email"
    className={inputCls}
    value={form.email}
    onChange={set('email')}
    required
    pattern="^[^\s@]+@[^\s@]+\.[^\s@]+$"
    title="Ingresá un correo válido, por ejemplo: cliente@gmail.com"
  />

  {form.email.trim() !== '' &&
    !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim()) && (
      <span className="mt-1 block text-xs text-red-600">
        Ingresá un correo electrónico válido, por ejemplo: cliente@gmail.com
      </span>
    )}
</label>
                  <label className="block">
                    <span className="mb-1.5 block text-[13px] font-semibold text-brand-navy">{c.form.phone}</span>
                    <input data-testid="contact-input-phone" className={inputCls} value={form.phone} onChange={set('phone')} required />
                  </label>
                  <label className="block">
                    <span className="mb-1.5 block text-[13px] font-semibold text-brand-navy">{c.form.country}</span>
                    <select data-testid="contact-select-country" className={inputCls} value={form.country} onChange={set('country')}>
                      {c.form.countries.map((o) => <option key={o.v} value={o.v}>{o.label}</option>)}
                    </select>
                  </label>
                  <label className="block sm:col-span-2">
                    <span className="mb-1.5 block text-[13px] font-semibold text-brand-navy">{c.form.vehicle}</span>
                    <select data-testid="contact-select-vehicle" className={inputCls} value={form.vehicle} onChange={set('vehicle')} required>
                      <option value="" disabled>—</option>
                      {c.form.vehicleTypes.map((v) => <option key={v} value={v}>{v}</option>)}
                    </select>
                  </label>
                  <label className="block sm:col-span-2">
                    <span className="mb-1.5 block text-[13px] font-semibold text-brand-navy">{c.form.message}</span>
                    <textarea data-testid="contact-input-message" rows={4} className={inputCls} placeholder={c.form.messagePh} value={form.message} onChange={set('message')} />
                  </label>
                  {status === 'error' && (
                    <p data-testid="contact-error-msg" className="flex items-center gap-2 rounded-xl bg-red-50 px-4 py-3 text-[13px] font-medium text-red-600 sm:col-span-2">
                      <AlertCircle className="h-4 w-4" />{c.form.error}
                    </p>
                  )}
                  <button
                    type="submit"
                    data-testid="contact-submit-btn"
                    disabled={!valid || status === 'sending'}
                    className="inline-flex items-center justify-center gap-2 rounded-full bg-brand-blue px-8 py-3.5 text-sm font-semibold text-white shadow-[0_16px_36px_-14px_rgba(30,159,224,0.9)] transition enabled:hover:bg-brand-bluedark disabled:cursor-not-allowed disabled:opacity-40 sm:col-span-2 sm:w-auto sm:justify-self-start"
                  >
                    <Send className="h-4 w-4" />
                    {status === 'sending' ? '...' : c.form.submit}
                  </button>
                </div>
              )}
            </form>
          </Reveal>

          <div className="space-y-5 lg:col-span-5">
            <Reveal delay={0.08}>
              <a href={waLink()} target="_blank" rel="noopener noreferrer" data-testid="contact-whatsapp-card" className="block rounded-2xl border border-brand-line bg-[#25D366]/5 p-7 transition hover:border-[#25D366]/60">
                <MessageCircle className="h-7 w-7 text-[#128C4B]" />
                <h2 className="mt-4 font-display text-lg font-bold text-brand-navy">{c.whatsappTitle}</h2>
                <p className="mt-2 text-sm leading-relaxed text-brand-muted">{c.whatsappDesc}</p>
              </a>
            </Reveal>
            <Reveal delay={0.14}>
              <div className="rounded-2xl border border-brand-line bg-white p-7" data-testid="contact-email-card">
                <Mail className="h-6 w-6 text-brand-blue" />
                <p className="mt-3 text-[12px] font-semibold uppercase tracking-[0.16em] text-brand-muted">{c.emailLabel}</p>
                <a href={`mailto:${content.footer.email}`} className="text-[15px] font-semibold text-brand-navy transition hover:text-brand-blue">{content.footer.email}</a>
              </div>
            </Reveal>
            {lang === 'es' && content.countryPicker && (
              <Reveal delay={0.18}>
                <div className="rounded-2xl border border-brand-line bg-white p-5" data-testid="contact-country-picker">
                  <p className="text-[12px] font-semibold uppercase tracking-[0.16em] text-brand-muted">{content.countryPicker.label}</p>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {content.countryPicker.options.map((o) => (
                      <button
                        key={o.v}
                        data-testid={`country-option-${o.v}`}
                        onClick={() => setCountry(o.v)}
                        className={`rounded-full px-4 py-2 text-[12.5px] font-semibold transition ${country === o.v ? 'bg-brand-blue text-white' : 'border border-brand-line text-brand-navy hover:border-brand-blue'}`}
                      >
                        {o.label}
                      </button>
                    ))}
                  </div>
                </div>
              </Reveal>
            )}
          </div>
        </div>
      </section>

      <section className="bg-brand-mist py-16 lg:py-24" data-testid="contact-sedes">
        <div className="container-x">
          <Reveal>
            <h2 className="font-display text-3xl font-bold tracking-tight text-brand-navy sm:text-4xl">{c.sedesTitle}</h2>
            <p className="mt-3 max-w-xl text-base text-brand-muted sm:text-lg">{c.sedesSubtitle}</p>
          </Reveal>
          <div className="mt-10 grid gap-6 lg:grid-cols-3">
            {content.sedes.map((s, i) => (
              <Reveal key={s.id} delay={i * 0.07}>
                <article data-testid={`contact-sede-card-${s.id}`} className="flex h-full flex-col overflow-hidden rounded-2xl border border-brand-line bg-white">
                  <div className="overflow-hidden border-b border-brand-line">
                    <iframe
                      title={`Mapa — ${s.city}`}
                      src={osmEmbed(s.lat, s.lon)}
                      className="h-[190px] w-full"
                      loading="lazy"
                    />
                  </div>
                  <div className="flex flex-1 flex-col p-6">
                    <span className="inline-flex w-fit rounded-full bg-brand-ice px-3 py-1 text-[10px] font-bold uppercase tracking-[0.14em] text-brand-blue">{s.badge}</span>
                    <h3 className="mt-3 font-display text-xl font-bold text-brand-navy">{s.city}</h3>
                    <p className="text-[13px] text-brand-muted">{s.country}</p>
                    <ul className="mt-4 flex-1 space-y-3 text-[13.5px] text-brand-navy/85">
                      <li className="flex gap-2.5"><MapPin className="mt-0.5 h-4 w-4 shrink-0 text-brand-blue" />{s.address}</li>
                      <li className="flex gap-2.5"><Phone className="mt-0.5 h-4 w-4 shrink-0 text-brand-blue" /><a href={`tel:${s.phone.replace(/\D/g, '')}`} data-testid={`sede-phone-${s.id}`} className="transition hover:text-brand-blue">{s.phone}</a></li>
                      <li className="flex gap-2.5"><MessageCircle className="mt-0.5 h-4 w-4 shrink-0 text-brand-blue" /><a href={sedeWaLink(s)} target="_blank" rel="noopener noreferrer" data-testid={`sede-whatsapp-${s.id}`} className="transition hover:text-brand-blue">{s.whatsapp}</a></li>
                      <li className="flex gap-2.5"><Clock className="mt-0.5 h-4 w-4 shrink-0 text-brand-blue" />{s.hours}</li>
                    </ul>
                    <a
                      href={osmLink(s.lat, s.lon)}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`sede-map-link-${s.id}`}
                      className="mt-5 inline-flex items-center gap-1.5 text-[13px] font-semibold text-brand-blue transition hover:gap-2.5"
                    >
                      {c.labels.mapLink}
                      <ExternalLink className="h-3.5 w-3.5" />
                    </a>
                  </div>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}